import { useState, useEffect, useRef } from 'react';
import { apiClient } from '@/lib/api/client';

interface ServerStatus {
  online: boolean;
  players: {
    online: number;
    max: number;
  };
  version: string;
}

interface MinecraftStatusResponse {
  online: boolean;
  players: { online: number; max: number };
  version: string;
  latency: number | null;
}

const POLL_ONLINE_MS  = 60_000;  // re-check every 60s when online
const POLL_OFFLINE_MS = 25_000;  // retry every 25s when offline (API cache expires at 20s)

export function useMinecraftServerStatus(serverIp: string, gameType?: 'HYTALE' | 'MINECRAFT' | 'DIGITAL') {
  const [status, setStatus] = useState<ServerStatus | null>(null);
  const [loading, setLoading] = useState(gameType === undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (gameType === undefined) return;
    if (!serverIp || gameType !== 'MINECRAFT') {
      setLoading(false);
      return;
    }

    let mounted = true;

    function clearTimer() {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    async function fetchStatus() {
      clearTimer();
      try {
        const [host, port] = serverIp.split(':');
        const params = new URLSearchParams({ host });
        if (port) params.append('port', port);

        const data = await apiClient.get<MinecraftStatusResponse>(
          `/minecraft/status?${params}`,
        );

        if (!mounted) return;

        const next: ServerStatus = {
          online: data.online,
          players: {
            online: data.players?.online ?? 0,
            max: data.players?.max ?? 0,
          },
          version: data.version || '',
        };
        setStatus(next);

        // Schedule next poll — sooner if offline so we recover quickly
        timerRef.current = setTimeout(fetchStatus, next.online ? POLL_ONLINE_MS : POLL_OFFLINE_MS);
      } catch {
        if (!mounted) return;
        setStatus({ online: false, players: { online: 0, max: 0 }, version: '' });
        // Retry soon on error
        timerRef.current = setTimeout(fetchStatus, POLL_OFFLINE_MS);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    setLoading(true);
    fetchStatus();

    return () => {
      mounted = false;
      clearTimer();
    };
  }, [serverIp, gameType]);

  return { status, loading };
}
